import { faGear } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Divider,
  MenuItem,
  Popover,
  Select,
  Switch,
  Typography,
} from '@mui/material';
import React from 'react';
import {
  AFTER_RELEASE,
  type AfterReleaseChoice,
  type UwPreferences,
} from '../../hooks/useUwPreferences';

const AFTER_RELEASE_OPTIONS: ReadonlyArray<{
  readonly value: AfterReleaseChoice;
  readonly label: string;
}> = [
  { value: AFTER_RELEASE.ASK, label: 'Ask me every time' },
  { value: AFTER_RELEASE.INBOX, label: 'Underwriter inbox' },
  { value: AFTER_RELEASE.FOLLOW_QUOTE, label: 'Follow the quote' },
  { value: AFTER_RELEASE.ALL_QUOTES, label: 'All quotes' },
];

interface UwPreferencesMenuProps {
  readonly preferences: UwPreferences;
  readonly onToggle: <K extends keyof UwPreferences>(
    key: K,
    value: UwPreferences[K]
  ) => void;
}

const UwPreferencesMenu = ({
  preferences,
  onToggle,
}: UwPreferencesMenuProps) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        onClick={handleOpen}
        startIcon={<FontAwesomeIcon icon={faGear} />}
        sx={{
          color: 'text.secondary',
          textTransform: 'none',
          fontSize: '0.8125rem',
          whiteSpace: 'nowrap',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        UW Preferences
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 280, mt: 0.5 } }}
      >
        <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
            }}
          >
            Preferences
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ px: 2, py: 1.5 }}>
          <PreferenceRow
            label="Sticky Header"
            description="Keep header visible while scrolling"
            checked={preferences.isStickyHeader}
            onChange={(checked) => onToggle('isStickyHeader', checked)}
          />
        </Box>

        <Divider />

        {/* FIR-21161: the way back out of a remembered release destination. */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            After releasing a quote
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Where to go once a quote is released
          </Typography>
          <Select
            size="small"
            fullWidth
            sx={{ mt: 1 }}
            data-testid="after-release-preference"
            value={preferences.afterRelease ?? AFTER_RELEASE.ASK}
            onChange={(event) =>
              onToggle('afterRelease', event.target.value as AfterReleaseChoice)
            }
          >
            {AFTER_RELEASE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Popover>
    </>
  );
};

export default UwPreferencesMenu;

function PreferenceRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Switch
        size="small"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </Box>
  );
}
